import React, {useState, useEffect, useRef} from "react";
const moment = require("moment");

import {useObserver, observer} from "mobx-react-lite";
import {useHistory} from "react-router-dom";
import {Col, Row, Tabs, Tooltip} from "antd";
import {useTranslation} from "react-i18next";
import {CompanyFilter, SearchText} from "../../_defaultComponents/FilterComponents";
import useIsFocused from "../../../hooks/useIsFocused";
import {useFetch, useStore} from "@vigo/hooks";
import {deliveryStatuses} from "../../../constants/constants";
import {isConstantExist, isNumeric} from "../../../utils/helpers";
import {CustomTab, SelectField, TabRow} from "./CustomerOrdersPage.styles";
import {CustomerOrdersTable} from "./customerOrdersTable/CustomerOrdersTable";
import {CustomerFluidSubPageLayout} from "../../layouts/customerLayout/customerFluidSubPageLayout/CustomerFluidSubPageLayout";
import ActiveOrderIcon from "../../../public/images/active-order-icon.svg";
import WaitingOrderIcon from "../../../public/images/waiting-order-icon.svg";
import CanceledOrderIcon from "../../../public/images/canceled-order-icon.svg";
import DeliveredOrderIcon from "../../../public/images/delivered-order-icon.svg";
import TotalOrderIcon from "../../../public/images/total-order-icon.svg";
import {CustomerMenuHeader} from "components/layouts/customerLayout/components/customerMenuHeader";
import {TimeRangeFilter} from "../customerHomePage/orderStats/timeRangeFilter/TimeRangeFilter";

const CustomerOrdersPage = observer(() => {
    const history = useHistory();
    const stores = useStore();
    const {t} = useTranslation(["orderPage", "deliveryStatuses"]);
    const isFocused = useIsFocused();
    const [currentPage, setCurrentPage] = useState(1);
    const [searchText, setSearchText] = useState("");
    const [text, setText] = useState("");
    const [call, setCall] = useState(false);
    const [field, setField] = useState({field: "", asc: true});
    const [sortField, setSortField] = useState({field: "", asc: true});
    const [recall, setRecall] = useState(true);
    const recallRef = useRef(recall);
    const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
    const [dateType, setDateType] = useState("daily");
    recallRef.current = recall;

    const [statsRecall, setStatsRecall] = useState(true);
    const statsRecallRef = useRef(statsRecall);
    statsRecallRef.current = statsRecall;

    const filters = stores.meStore.orderPage?.filters;
    const filterStartDate = filters.dateRange.length > 0 ? filters.dateRange[0] : undefined;
    const filterEndDate = filters.dateRange.length > 0 ? filters.dateRange[1] : undefined;

    const selectedSubCompanyId = stores.meStore.selectedSubCompanyId;
    const selectedSubCompanies = React.useMemo(() => {
        return selectedSubCompanyId ? [selectedSubCompanyId] : [];
    }, [selectedSubCompanyId]);

    const [statsLoading, statsData] = useFetch({
        fetchFn: stores.statisticStore.getHistoricStatistics.bind(null, {
            period: dateType,
            city: "",
            startDate: startDate,
            endDate: endDate,
            subcompanies: selectedSubCompanies.join("|")
        }),
        defaultDataValue: {},
        initialCall: true,
        dependencies: [statsRecall, dateType, startDate, endDate, selectedSubCompanies]
    });

    const buildOrderListParams = React.useMemo(() => {
        const baseParams = {
            limit: 10,
            skip: (currentPage - 1) * 10,
            companies: filters.companies,
            subcompanies: selectedSubCompanies,
            //city: stores.meStore.map.filters.city,
            deliveryStatus: filters.orderType,
            searchText,
            sortField,
            deliveryStatusOverrides: filters.deliveryStatusOverrides
        };
        if (filters.orderType !== "current" && filters.orderType !== "inQueue") {
            baseParams.startDate = filterStartDate ?? startDate;
            baseParams.endDate = filterEndDate ?? endDate;
        }

        return baseParams;
    }, [
        currentPage,
        filters.companies,
        selectedSubCompanies,
        filters.orderType,
        searchText,
        sortField,
        filters.deliveryStatusOverrides,
        filterStartDate,
        filterEndDate,
        startDate,
        endDate
    ]);

    const [orderListLoading, orderList, setOrderList, setOrderListLoading] = useFetch({
        fetchFn: stores.orderStore.getOrderList.bind(null, buildOrderListParams),
        dependencies: [
            currentPage,
            //stores.meStore.map.filters.city,
            filters.companies,
            searchText,
            call,
            recall,
            filters.orderType,
            sortField,
            filters.deliveryStatusOverrides,
            filterStartDate,
            filterEndDate,
            startDate,
            endDate,
            dateType,
            selectedSubCompanies
        ],
        initialCall: true
    });

    useEffect(() => {
        if (isFocused) {
            let orderTimeout = null;
            let statsTimeout = null;

            if (
                filters.orderType === "current" ||
                filters.orderType === "burningOrders" ||
                filters.orderType === "inQueue" ||
                filters.orderType === "inSlot" ||
                filters.orderType === "inPrep"
            ) {
                orderTimeout = setTimeout(() => {
                    setRecall(!recallRef.current);
                }, 10000);
            }

            // Stats için de periyodik güncelleme
            statsTimeout = setTimeout(() => {
                setStatsRecall(!statsRecallRef.current);
            }, 10000); // 10 saniyede bir güncelle

            return () => {
                if (orderTimeout) clearTimeout(orderTimeout);
                if (statsTimeout) clearTimeout(statsTimeout);
            };
        }
    }, [recall, statsRecall, filters.orderType, isFocused]);

    useEffect(() => {
        setStartDate(moment().format("YYYY-MM-DD"));
        setEndDate(moment().format("YYYY-MM-DD"));
        setDateType("daily");
        setStatsRecall(!statsRecallRef.current);
        setCurrentPage(1);
        setCall(!call);
    }, [selectedSubCompanyId]);

    useEffect(() => {
        const urlSearchParams = new URLSearchParams(history.location.search);
        const urlPage = urlSearchParams.get("page");

        //let urlCity = urlSearchParams.get("city");
        let urlCompanies = urlSearchParams.get("companies");
        const urlSubCompanies = urlSearchParams.get("subcompanies");
        const urlSearchText = urlSearchParams.get("searchText");
        const urlOrderType = urlSearchParams.get("orderType");
        const urlDeliveryStatus = urlSearchParams.get("deliveryStatus");

        if (urlPage) {
            const page = parseInt(urlPage);
            if (!isNaN(page)) setCurrentPage(page);
        }

        if (urlCompanies) {
            urlCompanies = urlCompanies.split("-");
            stores.meStore.orderPage.filters.companies = urlCompanies;
        }

        if (urlSubCompanies) {
            const subCompanyIds = urlSubCompanies.split("-");
            if (subCompanyIds.length > 0) {
                stores.meStore.setSelectedSubCompanyId(subCompanyIds[0]);
            }
        }

        if (urlSearchText) {
            setSearchText(urlSearchText);
            setText(urlSearchText);
        }
        if (urlOrderType) {
            stores.meStore.orderPage.filters.orderType = urlOrderType;
        }
        if (urlDeliveryStatus) {
            stores.meStore.orderPage.filters.deliveryStatusOverrides = [urlDeliveryStatus];
        }
    }, []);

    useEffect(() => {
        setSortField(field);
        setUrl();
    }, [
        currentPage,
        //stores.meStore.map.filters.city,
        filters.companies,
        searchText,
        filters.orderType,
        field.asc,
        filters.deliveryStatusOverrides,
        selectedSubCompanyId
    ]);

    const setUrl = () => {
        let path = history.location.pathname;
        if (currentPage) {
            path += "?page=" + currentPage;
        }
        /* if (stores.meStore.map.filters.city !== "") {
            path += "&city=" + stores.meStore.map.filters.city;
        } */
        if (filters.companies.length) {
            path += "&companies=" + filters.companies?.join("-");
        }
        if (selectedSubCompanyId) {
            path += "&subcompanies=" + selectedSubCompanyId;
        }
        if (searchText) {
            path += "&searchText=" + searchText;
        }
        if (filters.orderType) {
            path += "&orderType=" + filters.orderType;
        }

        history.push(path);
    };
    const changeTab = value => {
        setCurrentPage(1);
        setSearchText("");
        setText("");

        // Clear filters
        stores.meStore.orderPage.filters.dateRange = [];
        stores.meStore.orderPage.filters.companies = [];

        // Set order type and delivery status overrides
        stores.meStore.orderPage.filters.orderType = value;

        if (value === "cancelled") {
            stores.meStore.orderPage.filters.deliveryStatusOverrides = ["canceled"];
            stores.meStore.orderPage.filters.orderType = "past";
        } else if (value === "past") {
            stores.meStore.orderPage.filters.deliveryStatusOverrides = ["delivered"];
        } else {
            stores.meStore.orderPage.filters.deliveryStatusOverrides = [];
        }

        // Force data refresh
        setCall(!call);
        setRecall(!recallRef.current);
    };

    const tabIsActive = tab => {
        if (tab === "cancelled") {
            return (
                filters.orderType === "past" &&
                filters.deliveryStatusOverrides.includes("canceled") &&
                filters.deliveryStatusOverrides.length === 1
            );
        } else if (tab === "past") {
            return (
                filters.orderType === "past" &&
                !filters.deliveryStatusOverrides.includes("cancelled") &&
                filters.deliveryStatusOverrides.includes("delivered")
            );
        }

        return tab === filters.orderType;
    };

    const nullObjectCheck = obj => {
        return JSON.stringify(obj) === JSON.stringify({});
    };

    const renderActiveOrderCount = () => {
        if (
            statsLoading ||
            nullObjectCheck(statsData) ||
            !isNumeric(statsData?.onTheWayOrdersCount)
        ) {
            return "";
        }
        return statsData?.onTheWayOrdersCount;
    };

    const renderWaitingOrderCount = () => {
        if (
            statsLoading ||
            nullObjectCheck(statsData) ||
            !isNumeric(statsData?.inQueueOrdersCount)
        ) {
            return "";
        }
        return statsData?.inQueueOrdersCount;
    };

    const renderCanceledOrderCount = () => {
        if (
            statsLoading ||
            nullObjectCheck(statsData) ||
            !isNumeric(statsData?.canceledOrdersCount)
        ) {
            return "";
        }
        return statsData?.canceledOrdersCount;
    };

    const renderDeliveredOrderCount = () => {
        if (
            statsLoading ||
            nullObjectCheck(statsData) ||
            !isNumeric(statsData?.deliveredOrdersCount)
        ) {
            return "";
        }
        return statsData?.deliveredOrdersCount;
    };

    const renderTotalOrdersCount = () => {
        if (statsLoading || nullObjectCheck(statsData) || !isNumeric(statsData?.totalOrderCount)) {
            return "";
        }
        return statsData?.totalOrderCount;
    };

    return (
        <CustomerFluidSubPageLayout>
            <CustomerMenuHeader />
            {/* <OrderPage /> */}

            {/* ================================================== */}
            {/* Orders Tabs & Filters */}
            <Row type="flex" justify="center" data-testid="filterCard" style={{width: "100%"}}>
                {/* ================================================== */}
                {/* Orders Tabs */}
                <TabRow>
                    <CustomTab
                        key="current"
                        isActive={tabIsActive("current")}
                        onClick={() => changeTab("current")}
                    >
                        <img src={ActiveOrderIcon} alt="Active Order Icon" />
                        <div>
                            <div>{renderActiveOrderCount()}</div>
                            <div>Aktif Paket Sayısı</div>
                        </div>
                    </CustomTab>
                    <CustomTab
                        key="inQueue"
                        isActive={tabIsActive("inQueue")}
                        onClick={() => changeTab("inQueue")}
                    >
                        <img src={WaitingOrderIcon} alt="Waiting Order Icon" />
                        <div>
                            <div>{renderWaitingOrderCount()}</div>
                            <div>Bekleyen Paket Sayısı</div>
                        </div>
                    </CustomTab>
                    <CustomTab
                        key="cancelled"
                        isActive={tabIsActive("cancelled")}
                        onClick={() => {
                            changeTab("cancelled");
                        }}
                    >
                        <img src={CanceledOrderIcon} alt="Canceled Order Icon" />
                        <div>
                            <div>{renderCanceledOrderCount()}</div>
                            <div>İptal Paket Sayısı</div>
                        </div>
                    </CustomTab>
                    <CustomTab
                        key="past"
                        isActive={tabIsActive("past")}
                        onClick={() => changeTab("past")}
                    >
                        <img src={DeliveredOrderIcon} alt="Delivered Order Icon" />
                        <div>
                            <div>{renderDeliveredOrderCount()}</div>
                            <div>Tamamlanmış Paket Sayısı</div>
                        </div>
                    </CustomTab>
                    <CustomTab
                        key="total"
                        isActive={tabIsActive("total")}
                        onClick={() => changeTab("total")}
                    >
                        <img src={TotalOrderIcon} alt="Delivered Order Icon" />
                        <div>
                            <div>{renderTotalOrdersCount()}</div>
                            <div>Toplam Paket Sayısı</div>
                        </div>
                    </CustomTab>

                    {/* <CustomTab
                        key="inSlot"
                        isActive={tabIsActive("inSlot")}
                        onClick={() => changeTab("inSlot")}
                    >
                        <img src={ActiveOrderIcon} alt="Active Order Icon" />
                        {t("filters.orderStatus.slotOrders", {ns: "orderPage"})}
                    </CustomTab> */}
                    {/* <CustomTab
                        key="inPrep"
                        isActive={tabIsActive("inPrep")}
                        onClick={() => changeTab("inPrep")}
                    >
                        <img src={ActiveOrderIcon} alt="Active Order Icon" />
                        {t("filters.orderStatus.inPreparationOrders", {ns: "orderPage"})}
                    </CustomTab> */}
                    {/* {stores.meStore.checkRole("notVisibleForCompanyUser") && (
                        <CustomTab
                            key="burningOrders"
                            isActive={tabIsActive("burningOrders")}
                            onClick={() => changeTab("burningOrders")}
                        >
                            <img src={ActiveOrderIcon} alt="Active Order Icon" />
                            {t("filters.orderStatus.burningOrders", {ns: "orderPage"})}
                        </CustomTab>
                    )} */}
                </TabRow>
                <Col>
                    <Tabs
                        activeKey={filters.orderType}
                        onChange={changeTab}
                        className="orderTabs"
                    ></Tabs>
                </Col>
                {/* ================================================== */}
                {/* Orders Filters */}
                <Col span={24}>
                    <Row type="flex" style={{gap: "16px"}}>
                        {filters.orderType !== "inQueue" &&
                            filters.orderType !== "inSlot" &&
                            filters.orderType !== "inPrep" && (
                                <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                    <h5>{t("fields.deliveryStatus", {ns: "common"})}</h5>
                                    <SelectField
                                        value={filters.deliveryStatusOverrides}
                                        onChange={value => {
                                            setCurrentPage(1);
                                            stores.meStore.orderPage.filters.deliveryStatusOverrides = value;
                                            // Force data refresh
                                            setCall(!call);
                                        }}
                                        mode="multiple"
                                        placeholder={t("filters.addFilter", {ns: "common"})}
                                        data-testid="deliveryStatusSelect"
                                    >
                                        {deliveryStatuses.map(deliveryStatusObj => {
                                            if (deliveryStatusObj.type === filters.orderType) {
                                                return (
                                                    <SelectField.Option
                                                        key={deliveryStatusObj.value}
                                                        value={deliveryStatusObj.value}
                                                        data-testid="deliveryStatusSelect_option"
                                                    >
                                                        {t(
                                                            `${deliveryStatusObj.translation_key}.shortName`,
                                                            {
                                                                ns: "deliveryStatuses"
                                                            }
                                                        )}
                                                    </SelectField.Option>
                                                );
                                            }
                                        })}
                                    </SelectField>
                                </Col>
                            )}
                        {!stores.meStore.checkRole("isCompanyUser") && (
                            <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                <CompanyFilter
                                    showPool={false}
                                    value={filters.companies}
                                    onChange={value => {
                                        setCurrentPage(1);
                                        stores.meStore.orderPage.filters.companies = value;
                                        // Force data refresh
                                        setCall(!call);
                                    }}
                                    mode="multiple"
                                    placeholder={t("filters.addFilter", {ns: "common"})}
                                />
                            </Col>
                        )}

                        <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{marginBottom: "16px"}}>
                            <SearchText
                                showLabel={true}
                                showPlaceholder={true}
                                value={searchText}
                                onChange={value => {
                                    setSearchText(value);
                                    setText(value);
                                    setCurrentPage(1);
                                    // Force data refresh when search text changes
                                    setCall(!call);
                                }}
                            />
                        </Col>
                        <Col
                            xs={24}
                            sm={12}
                            md={8}
                            lg={6}
                            xl={4}
                            style={{
                                padding: "0px",
                                width: "100%",
                                minWidth: "unset",
                                maxWidth: "unset"
                            }}
                        >
                            <TimeRangeFilter
                                dateType={dateType}
                                selectFieldOnChange={value => {
                                    if (value === "daily") {
                                        setStartDate(moment().format("YYYY-MM-DD"));
                                        setEndDate(moment().format("YYYY-MM-DD"));
                                    } else if (value === "weekly") {
                                        setStartDate(
                                            moment()
                                                .subtract(1, "week")
                                                .format("YYYY-MM-DD")
                                        );
                                        setEndDate(moment().format("YYYY-MM-DD"));
                                    } else if (value === "monthly") {
                                        setStartDate(
                                            moment()
                                                .subtract(1, "month")
                                                .format("YYYY-MM-DD")
                                        );
                                        setEndDate(moment().format("YYYY-MM-DD"));
                                    } else if (["choose", ""].includes(value)) {
                                        setStartDate("");
                                        setEndDate("");
                                    }
                                    setDateType(value);
                                    // Stats verilerini güncelle
                                    setStatsRecall(!statsRecallRef.current);
                                    setCurrentPage(1);
                                    // Force data refresh
                                    setCall(!call);
                                }}
                                rangePickerOnChange={formatString => {
                                    setStartDate(formatString[0]);
                                    setEndDate(formatString[1]);
                                    // Stats verilerini güncelle
                                    setStatsRecall(!statsRecallRef.current);
                                    setCurrentPage(1);
                                    // Force data refresh
                                    setCall(!call);
                                }}
                                isOrdersPage={true}
                                showInfoTooltip={
                                    filters.orderType === "current" ||
                                    filters.orderType === "inQueue"
                                }
                                tooltipText="Aktif ve Bekleyen siparişler için tarih filtresi uygulanmaz."
                            />
                        </Col>
                        {/* <Col xs={12} sm={8} md={8} lg={4} xl={3}>
                                 <CityFilter
                                     value={stores.meStore.map.filters.city}
                                     onChange={value => {
                                         setCurrentPage(1);
                                         stores.meStore.map.filters.city = value;
                                         stores.meStore.orderPage.filters.regionGroups = [];
                                         stores.meStore.orderPage.filters.upperGroup = "all";
                                         stores.meStore.orderPage.filters.selectedSubCompanies = [];
                                     }}
                                 />
                             </Col> */}
                    </Row>
                </Col>
            </Row>

            {/* ================================================== */}
            {/* Orders Table */}
            <CustomerOrdersTable
                loading={orderListLoading}
                data={orderList.rows}
                dataCount={orderList.dataCount}
                orderType={filters.orderType}
                currentPage={currentPage}
                field={field}
                setField={setField}
                setPage={async page => {
                    setCurrentPage(page);
                    history.push("/musteri/siparislerim?page=" + page);
                }}
                setRecall={setRecall}
                recallRef={recallRef}
                setStatsRecall={setStatsRecall}
            />
        </CustomerFluidSubPageLayout>
    );
});

export default CustomerOrdersPage;
